import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setChannel, selectHasSingleBrandAndStory, selectHasSingleBrand } from '../features/channelSlice';
import { toggleSidePanel } from '../features/sidePanelSlice';
import { selectIsAuthenticated, signOut } from '../features/authSlice';
import { openLiveModal } from '../features/liveSlice';
import ToastNotification from './ToastNotification';
import AudioDevicePopover from './AudioDevicePopover';
import { sendCaraQuit } from '../utils/caraBridge';
import { resetExperience, teardownExperience } from '../utils/resetExperience';

/**
 * FooterCarousel - A simple, always-at-bottom navigation bar
 * Shows action buttons instead of channels
 */
export default function FooterCarousel({ onHomeClick, onShowSettings, onShowBrandSwitcher, toastMessages = [], onRemoveToast }) {
  const dispatch = useDispatch();
  
  const currentChannel = useSelector((state) => state.channel.currentChannel);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const hasSingleBrandAndStory = useSelector(selectHasSingleBrandAndStory);
  const hasSingleBrand = useSelector(selectHasSingleBrand);
  const eventHistory = useSelector((state) => state.live.eventHistory);
  const toastsEnabled = useSelector((state) => state.notifications.toastsEnabled);
  const sidePanelIconSrc = useSelector((state) => state.channel.sidePanelIconSrc);
  const isLightMode = useSelector((state) => state.device.theme) === 'light';
  // Inactive icon/label colour — light grey on the dark bar, dark grey on the light bar.
  const inactiveColor = isLightMode ? 'rgba(55, 65, 81, 0.85)' : 'rgba(203, 213, 225, 0.8)';
  const inactiveLabelColor = isLightMode ? 'rgba(75, 85, 99, 0.85)' : 'rgba(203, 213, 225, 0.7)';
  // Active/highlighted icon + label — use a deeper blue on the light bar so it
  // stays readable (the light-blue tints wash out on white).
  const activeColor = isLightMode ? '#1d4ed8' : '#60a5fa';
  const activeLabelColor = isLightMode ? '#1d4ed8' : '#bfdbfe';

  // Needed by Reset to replay onboarding events from config
  const onboardingEvents = useSelector((state) => state.channel.onboardingEvents);
   // Check if authentication is enabled
  const authEnabled = process.env.REACT_APP_SKIP_AUTH !== 'true';
  
  const handleHomeClick = () => {
    if (onHomeClick) {
      onHomeClick();
    } else {
      dispatch(setChannel('home'));
    }
  };

  const handleHelp = () => {
    dispatch({ type: 'channel/toggleHelpModal' });
  };
  
  const handleReset = () => {
    console.log('🔄 RESET (footer): tap received');
    if (!window.confirm("Are you sure you want to reset all messages, emails, and notifications?")) return;
    resetExperience({ dispatch, onboardingEvents });
  };

  const handleSignOut = async () => {
    if (sendCaraQuit()) {
      teardownExperience({ dispatch });
      return;
    }

    if (!window.confirm("Are you sure you want to sign out?")) return;

    await teardownExperience({ dispatch });
    dispatch(signOut());
  };

  const handleEmployeeClick = () => {
    dispatch(toggleSidePanel());
  };

  const handleLive = () => {
    dispatch(openLiveModal());
  };

  const handleTour = () => {
    // Start the guided tour
    if (window.startGuidedTour) {
      window.startGuidedTour();
    }
  };

  const [showAudioDevices, setShowAudioDevices] = React.useState(false);

  // Action buttons configuration - order matches desktop action bar
  const hasEventsToRecap = eventHistory && eventHistory.length > 0;
  const actionButtons = [
    // Recap button first (if there are events to recap)
    ...(hasEventsToRecap ? [{ icon: 'videocam', label: 'Recap', onClick: handleLive }] : []),
    // Settings - consolidated settings (opens modal on mobile)
    { icon: 'settings', label: 'Settings', onClick: () => setShowAudioDevices(!showAudioDevices), active: showAudioDevices },
    // Brand Switcher - Only show if user has multiple brands
    ...(!hasSingleBrand ? [{ icon: 'palette', label: 'Brand', onClick: () => onShowBrandSwitcher && onShowBrandSwitcher() }] : []),
    // Only show Story button if user has multiple brands or configurations
    ...(hasSingleBrandAndStory ? [] : [{ icon: 'auto_stories', label: 'Story', onClick: () => onShowSettings && onShowSettings() }]),
    { icon: 'route', label: 'Tour', onClick: handleTour }, // Add Tour button - using route icon (path)
    { icon: 'help_outline', label: 'Help', onClick: handleHelp },
    { icon: 'refresh', label: 'Reset', onClick: handleReset },
  ];

  // Add logout button if authenticated
  if (authEnabled && isAuthenticated) {
    actionButtons.push({ icon: 'logout', label: 'Quit', onClick: handleSignOut });
  }

  // Helper function to render an action button
  const renderActionButton = (button, key) => {
    const btn = (
      <button
        key={button.popover ? undefined : key}
        onClick={button.disabled ? undefined : button.onClick}
        disabled={button.disabled}
        className="footer-button"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',
        padding: '6px 6px',
        background: button.disabled
          ? 'rgba(30, 35, 45, 0.3)'
          : button.active
            ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(37, 99, 235, 0.2) 100%)'
            : 'transparent',
        border: button.disabled
          ? '1px solid rgba(100, 100, 100, 0.2)'
          : button.active ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid transparent',
        borderRadius: '10px',
        cursor: button.disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        minWidth: '44px',
        flex: '0 0 auto',
        opacity: button.disabled ? 0.4 : 1,
        // iPad: opt out of the 300ms tap delay and gesture detection that can
        // swallow clicks on buttons inside a horizontal -webkit-overflow-scrolling
        // container.
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'rgba(59, 130, 246, 0.25)',
        boxShadow: button.active
          ? '0 0 20px rgba(59, 130, 246, 0.2), inset 0 0 15px rgba(59, 130, 246, 0.1)'
          : 'none'
      }}
      onMouseEnter={(e) => {
        if (button.disabled) return;
        if (!button.active) {
          e.currentTarget.style.background = 'rgba(148, 163, 184, 0.12)';
          e.currentTarget.style.border = '1px solid rgba(148, 163, 184, 0.2)';
        } else {
          e.currentTarget.style.boxShadow = '0 0 24px rgba(59, 130, 246, 0.3), inset 0 0 20px rgba(59, 130, 246, 0.15)';
        }
      }}
      onMouseLeave={(e) => {
        if (button.disabled) return;
        if (!button.active) {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.border = '1px solid transparent';
        } else {
          e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.2), inset 0 0 15px rgba(59, 130, 246, 0.1)';
        }
      }}
    >
      <span 
        className="material-icons-outlined"
        style={{
          fontSize: '20px',
          color: button.disabled 
            ? 'rgba(100, 100, 100, 0.5)'
            : button.active ? activeColor : inactiveColor,
          transition: 'all 0.3s ease',
          filter: button.active ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))' : 'none'
        }}
      >
        {button.icon}
      </span>
      <span
        style={{
          fontSize: '8px',
          color: button.disabled 
            ? 'rgba(100, 100, 100, 0.5)'
            : button.active ? activeLabelColor : inactiveLabelColor,
          fontWeight: '600',
          letterSpacing: '0.3px',
          transition: 'all 0.3s ease',
          whiteSpace: 'nowrap',
          textShadow: button.active ? '0 0 8px rgba(59, 130, 246, 0.5)' : 'none'
        }}
      >
        {button.label}
      </span>
    </button>
    );

    return <React.Fragment key={key}>{btn}</React.Fragment>;
  };

  return (
    <>
      {/* Add responsive styles for toast positioning */}
      <style>{`
        @media (min-width: 1024px) {
          .toast-stack-container {
            left: auto !important;
            right: -300px !important;
            transform: translateY(50%) !important;
          }
        }
        .action-buttons-scroll::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      
      <div
        className="footer-carousel"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          // iPad/iOS home-indicator inset eats taps in the bottom ~20pt of the
          // viewport. Pad the bottom by the safe-area inset and grow the height
          // to match so the action row stays above it.
          height: 'calc(80px + env(safe-area-inset-bottom))',
          minWidth: 0, // Allow shrinking
          background: isLightMode
            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(241, 243, 244, 0.94) 50%, rgba(255, 255, 255, 0.96) 100%)'
            : 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.92) 50%, rgba(15, 23, 42, 0.95) 100%)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderTop: isLightMode ? '1px solid rgba(0, 0, 0, 0.10)' : '1px solid rgba(148, 163, 184, 0.15)',
          boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.4), 0 -2px 8px rgba(59, 130, 246, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px', // Reduced gap for better fit on small screens
          padding: '0 4px env(safe-area-inset-bottom)', // Reduced padding for mobile + iOS safe area
          zIndex: 9999,
          boxSizing: 'border-box',
          overflow: 'visible', // Allow content to be visible
        }}
      >
        {/* Customer Button (Home) - fixed width, don't shrink */}
      <button
        onClick={handleHomeClick}
        className="footer-button"
        data-tour-target="footer-customer"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2px',
          padding: '8px 8px',
          background: currentChannel === 'home' 
            ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(37, 99, 235, 0.2) 100%)' 
            : 'transparent',
          border: currentChannel === 'home' ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid transparent',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          minWidth: '50px',
          flex: '0 0 auto',
          boxShadow: currentChannel === 'home' 
            ? '0 0 20px rgba(59, 130, 246, 0.2), inset 0 0 15px rgba(59, 130, 246, 0.1)' 
            : 'none'
        }}
        onMouseEnter={(e) => {
          if (currentChannel !== 'home') {
            e.currentTarget.style.background = 'rgba(148, 163, 184, 0.12)';
            e.currentTarget.style.border = '1px solid rgba(148, 163, 184, 0.2)';
          } else {
            e.currentTarget.style.boxShadow = '0 0 24px rgba(59, 130, 246, 0.3), inset 0 0 20px rgba(59, 130, 246, 0.15)';
          }
        }}
        onMouseLeave={(e) => {
          if (currentChannel !== 'home') {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.border = '1px solid transparent';
          } else {
            e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.2), inset 0 0 15px rgba(59, 130, 246, 0.1)';
          }
        }}
      >
        <span 
          className="material-icons"
          style={{
            fontSize: '22px',
            color: currentChannel === 'home' ? activeColor : inactiveColor,
            transition: 'all 0.3s ease',
            filter: currentChannel === 'home' ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))' : 'none'
          }}
        >
          home
        </span>
        <span
          style={{
            fontSize: '9px',
            color: currentChannel === 'home' ? activeLabelColor : inactiveLabelColor,
            fontWeight: '600',
            letterSpacing: '0.3px',
            transition: 'all 0.3s ease',
            textShadow: currentChannel === 'home' ? '0 0 8px rgba(59, 130, 246, 0.5)' : 'none',
            whiteSpace: 'nowrap'
          }}
        >
          Customer
        </span>
      </button>

      {/* Divider */}
      <div 
        style={{
          width: '1px',
          height: '40px',
          background: 'linear-gradient(180deg, transparent 0%, rgba(148, 163, 184, 0.3) 50%, transparent 100%)',
          margin: '0 4px',
          boxShadow: '0 0 8px rgba(59, 130, 246, 0.1)',
          flexShrink: 0
        }}
      />

      {/* Middle Section: Either Toast Notifications or Action Buttons */}
      {toastsEnabled && toastMessages.length > 0 ? (
        // Toast Notification Stack - fills the space between Home and Employee
        <div
          className="toast-stack-container"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '1 1 0',
            minWidth: 0, // Critical for flex shrinking
            position: 'relative',
            minHeight: '64px',
            padding: '0 4px',
            overflow: 'hidden'
          }}
        >
          <div style={{ position: 'relative', width: '100%', maxWidth: '400px', minHeight: '64px' }}>
            {toastMessages.map((toast, index) => {
              // Reverse stacking: newest toast (last in array) should be on top
              const stackPosition = toastMessages.length - 1 - index;
              return (
                <div
                  key={toast.id}
                  style={{
                    position: 'absolute',
                    top: `${stackPosition * -6}px`, // Negative offset so newer toasts appear above
                    left: `${stackPosition * 3}px`,
                    right: 0,
                    zIndex: index + 1, // Higher index = higher z-index (newer on top)
                  }}
                >
                  <ToastNotification
                    subject={toast.subject}
                    body={toast.body}
                    icon={toast.icon}
                    onClose={() => onRemoveToast && onRemoveToast(toast.id)}
                    isMobile={true}
                    stackIndex={index}
                    totalToasts={toastMessages.length}
                    insideContainer={true}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        // Scrollable Action Buttons Container - only shown when no toasts
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center', // Center the buttons
            gap: '2px',
            flex: '1 1 0',
            minWidth: 0, // Critical for flex shrinking
            position: 'relative',
            overflow: 'hidden', // Clip but allow scrolling inside
          }}
        >
          {/* Left navigation caret */}
          <button
            onClick={() => {
              const container = document.querySelector('.action-buttons-scroll');
              if (container) container.scrollBy({ left: -80, behavior: 'smooth' });
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              minWidth: '20px',
              height: '44px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              opacity: 0.5,
              transition: 'opacity 0.2s',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
          >
            <span className="material-icons" style={{ fontSize: '16px', color: 'rgba(148, 163, 184, 0.9)' }}>chevron_left</span>
          </button>

          {/* Scrollable buttons */}
          <div
            className="action-buttons-scroll hide-scrollbar"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center', // Center when there's room
              gap: '6px',
              flex: '1 1 0',
              minWidth: 0,
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              padding: '4px 8px' // More horizontal padding to ensure buttons aren't clipped
            }}
          >
            {actionButtons.map((button, index) => renderActionButton(button, index))}
          </div>

          {/* Right navigation caret */}
          <button
            onClick={() => {
              const container = document.querySelector('.action-buttons-scroll');
              if (container) container.scrollBy({ left: 80, behavior: 'smooth' });
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              minWidth: '20px',
              height: '44px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              opacity: 0.5,
              transition: 'opacity 0.2s',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
          >
            <span className="material-icons" style={{ fontSize: '16px', color: 'rgba(148, 163, 184, 0.9)' }}>chevron_right</span>
          </button>
        </div>
      )}

      {/* Divider */}
      <div 
        style={{
          width: '1px',
          height: '40px',
          background: 'linear-gradient(180deg, transparent 0%, rgba(148, 163, 184, 0.3) 50%, transparent 100%)',
          margin: '0 4px',
          boxShadow: '0 0 8px rgba(59, 130, 246, 0.1)',
          flexShrink: 0
        }}
      />

      {/* Employee Panel Button - fixed width, don't shrink */}
      <button
        onClick={handleEmployeeClick}
        className="footer-button"
        data-tour-target="footer-employee"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2px',
          padding: '8px 8px',
          background: 'transparent',
          border: '1px solid transparent',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          minWidth: '50px',
          flex: '0 0 auto'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(148, 163, 184, 0.12)';
          e.currentTarget.style.border = '1px solid rgba(148, 163, 184, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.border = '1px solid transparent';
        }}
      >
        {sidePanelIconSrc ? (
          <img 
            src={sidePanelIconSrc} 
            alt="Employee"
            style={{
              width: '22px',
              height: '22px',
              objectFit: 'contain',
              transition: 'all 0.3s ease'
            }}
            onError={(e) => {
              // Fallback to work icon if image fails to load
              e.target.style.display = 'none';
              const fallbackIcon = e.target.nextSibling;
              if (fallbackIcon) {
                fallbackIcon.style.display = 'block';
              }
            }}
          />
        ) : null}
        <span 
          className="material-icons"
          style={{
            fontSize: '22px',
            color: inactiveColor,
            transition: 'all 0.3s ease',
            display: sidePanelIconSrc ? 'none' : 'block'
          }}
        >
          work
        </span>
        <span
          style={{
            fontSize: '9px',
            color: inactiveLabelColor,
            fontWeight: '600',
            letterSpacing: '0.3px',
            transition: 'all 0.3s ease',
            whiteSpace: 'nowrap'
          }}
        >
          Employee
        </span>
      </button>
    </div>

    {/* Settings Modal - Centered modal for mobile */}
    {showAudioDevices && (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          animation: 'fadeIn 0.3s ease-out',
        }}
      >
        {/* Backdrop */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          onClick={() => setShowAudioDevices(false)}
        />
        {/* Modal Content - same sizing as BrandSwitcher */}
        <div
          className="settings-modal-scroll"
          style={{
            position: 'relative',
            zIndex: 1,
            width: 'calc(100vw - 32px)',
            maxWidth: '420px',
            maxHeight: '70vh',
            overflowY: 'auto',
            animation: 'slideInModal 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            borderRadius: '16px',
          }}
        >
          <AudioDevicePopover
            isOpen={true}
            onClose={() => setShowAudioDevices(false)}
            anchor="none"
          />
        </div>
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideInModal {
            from {
              opacity: 0;
              transform: scale(0.92);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          .settings-modal-scroll::-webkit-scrollbar {
            width: 8px;
          }
          .settings-modal-scroll::-webkit-scrollbar-track {
            background: #111827;
          }
          .settings-modal-scroll::-webkit-scrollbar-thumb {
            background: #374151;
            border-radius: 4px;
          }
          .settings-modal-scroll::-webkit-scrollbar-thumb:hover {
            background: #4b5563;
          }
        `}</style>
      </div>
    )}
    </>
  );
}
